import type { ReactElement } from "react";
import {
  EQUIP_SLOTS,
  findNestedItem,
  MAX_MOD_DEPTH,
  getItemDef,
  type GridState,
  type Inventory,
  type ItemDef,
  type ItemInstance,
  type TraderDef,
} from "@rietz/shared";
import { useHud, type OpenContainer } from "../store.js";
import { PANEL_LINE, TEXT_DIM } from "../theme.js";
import { CELL, nestingRoots } from "./drag.js";
import { Panel } from "./panel.js";
import { PartTurntable } from "./partView.js";
import { TileBody } from "./tile.js";
import { ItemFacts, KIND_LABELS } from "./tooltip.js";

/**
 * "Inspect": the item, at a size you can actually look at. The tooltip is a
 * glance — 400 ms, dies with the pointer, inert. This is the same content
 * standing still: a bigger picture, the tooltip's own `ItemFacts` (so the two
 * never disagree), the whole description. An overlay `Panel`, draggable, and it
 * CO-EXISTS with other copies of itself — two side by side is how a player
 * compares.
 *
 * The picture is the tile's own, blown up — except for a PART, whose tile is
 * already a render, so it gets a live turntable (`PartTurntable`) to see the
 * other side. A GUN IS NEVER INSPECTED HERE: every screen routes a weapon to
 * the Werkbank (`menu.tsx`), read-only on the trader's counter.
 */

// ---------------------------------------------------------------------------
// Finding the item
// ---------------------------------------------------------------------------

/** An item the screen can reach, with its def already resolved. */
export interface InspectedItem {
  item: ItemInstance;
  def: ItemDef;
}

/**
 * The item `iid` names, if reachable from this screen — worn, in a carried
 * grid, in the open container, or inside a nested box (`findOwnedWeapon`'s walk
 * widened to every kind, over the NESTING roots so a corpse's helmet resolves).
 * `null` when unreachable or unknown — the dialog reads that as "close
 * yourself".
 */
export function findInspectItem(
  inv: Inventory,
  iid: string,
  container: OpenContainer | null = null,
): InspectedItem | null {
  for (const slot of EQUIP_SLOTS) {
    const worn = inv.equipment[slot];
    if (worn !== null && worn.iid === iid) return withDef(worn);
  }
  const roots = nestingRoots(inv, container);
  for (const grid of roots) {
    const hit = findNestedItem([grid], iid);
    if (hit !== null) return withDef(hit.item);
  }
  // THE PARTS: a scope screwed to a rifle is in no grid and no slot, so every
  // host reachable above is asked for its tree — a part on a rifle in a crate
  // resolves the same way the rifle does.
  const hosts: ItemInstance[] = [];
  for (const slot of EQUIP_SLOTS) {
    const worn = inv.equipment[slot];
    if (worn !== null) hosts.push(worn);
  }
  for (const grid of roots) collectItems(grid, hosts);
  for (const host of hosts) {
    const part = findModItem(host, iid, 0);
    if (part !== null) return withDef(part);
  }
  return null;
}

/** Every item in `grid` and in every box in it, into `out`. */
function collectItems(grid: GridState, out: ItemInstance[]): void {
  for (const p of grid.items) {
    out.push(p.item);
    if (p.item.contents !== undefined) collectItems(p.item.contents, out);
  }
}

/** The part `iid` names in `host`'s tree, at any legal depth. */
function findModItem(host: ItemInstance, iid: string, depth: number): ItemInstance | null {
  const mods = host.mods;
  if (mods === undefined || depth >= MAX_MOD_DEPTH) return null;
  for (const mod of Object.values(mods)) {
    if (mod.iid === iid) return mod;
    const deeper = findModItem(mod, iid, depth + 1);
    if (deeper !== null) return deeper;
  }
  return null;
}

function withDef(item: ItemInstance): InspectedItem | null {
  const def = getItemDef(item.defId);
  return def === null ? null : { item, def };
}

// ---------------------------------------------------------------------------
// The dialog
// ---------------------------------------------------------------------------

/**
 * How wide the column is, and therefore the dialog. Authored, not derived from
 * the subject — a 1×1 tin would collapse the panel, and the next item would
 * jump the window.
 */
const CONTENT_W = 300;

export function InspectDialog(props: {
  subject: InspectedItem;
  /** Only the trader screen passes one — adds the "Sells for" row. */
  trader?: TraderDef | null;
  /**
   * What this counter is ASKING for the thing, when the subject is a shelf
   * offer. Mutually exclusive with `trader`: "Sells for" is a number nobody
   * cares about here.
   */
  buyPrice?: number | null;
  /** Handed straight to the `Panel`: where this window first appears, so a
   *  stack of them lands staggered. See `PanelProps.nudge`. */
  nudge?: number;
  /** Handed straight to the `Panel`: a press in here raises the window over its
   *  siblings. See `PanelProps.onRaise`. */
  onRaise?: () => void;
  onClose: () => void;
}): ReactElement {
  const { item, def } = props.subject;
  // The same raid flag the tooltip reads: an uncounted magazine must not print
  // the number the tile withholds. Unconditional, as a hook must be.
  const inRaid = useHud((s) => s.screen === "raid");

  // The tile's own body at a bigger cell size, so the dialog shows exactly the
  // grid's artwork. Capped at 2.5× and the column's width.
  const scale = Math.min(2.5, 280 / (def.w * CELL), 200 / (def.h * CELL));

  return (
    <Panel
      overlay
      title={def.name}
      nudge={props.nudge}
      onRaise={props.onRaise}
      onClose={props.onClose}
    >
      <div style={{ width: CONTENT_W, display: "flex", flexDirection: "column", gap: 8 }}>
        {/* A PART gets the live model instead of a picture of one. See the
            header, and `PartTurntable`. */}
        {def.kind === "attachment" ? (
          <PartTurntable defId={def.id} />
        ) : (
          <div
            // No drag gesture of its own, so the browser's native image drag is
            // the
            // only thing a press could start. Refuse it.
            draggable={false}
            onDragStart={(ev) => {
              ev.preventDefault();
            }}
            style={{
              position: "relative",
              width: def.w * CELL * scale,
              height: def.h * CELL * scale,
              margin: "0 auto",
              userSelect: "none",
            }}
          >
            <TileBody def={def} item={item} iconOnly />
          </div>
        )}

        {/* The same line the tooltip prints under the name. */}
        <div style={{ fontSize: 11, color: TEXT_DIM }}>
          {KIND_LABELS[def.kind]} · {def.w}×{def.h}
        </div>

        <ItemFacts
          def={def}
          item={item}
          trader={props.trader ?? null}
          buyPrice={props.buyPrice ?? null}
          inRaid={inRaid}
        />

        {def.description === undefined ? null : (
          <div
            style={{
              marginTop: 6,
              paddingTop: 6,
              borderTop: `1px solid ${PANEL_LINE}`,
              fontSize: 11,
              color: TEXT_DIM,
              overflowWrap: "anywhere",
            }}
          >
            {def.description}
          </div>
        )}
      </div>
    </Panel>
  );
}
